import * as React from 'react';
import { classes, style } from 'typestyle';
import { Messages } from '../../gen-src/Localize';
import { ToolbarColorTheme, useToolbarColorThemeContext } from '../PublicTypes';
import { HiddenClassName } from '../styles/ButtonStyles';
import { GenericToolbarItem } from '../types/GenericToolbarItem';
import { Button } from './Button';
import { Dropdown } from './Dropdown';
import { PopoverButton } from './PopoverButton';
import { Separator } from './Separator';
import { SplitButton, SplitSecondaryIdSuffix } from './SplitButton';

export const ToolbarItemClass = 'toolbar-item';
export const ToolbarOverflowItemClass = 'toolbar-overflow-item';
const focusableButtonSelector = 'button:enabled:not(.hidden)';
const ItemContainerStyles: React.CSSProperties = { display: 'flex', alignItems: 'center', height: '100%' };
const GenericToolbarStyleName = style({
  $debugName: 'generictoolbar',
  $nest: { [`& .${HiddenClassName}`]: { display: 'none' } },
});

export interface GenericToolbarProps {
  /** Items left aligned in toolbar */
  leftAlignItems: GenericToolbarItem[];
  /** Items right aligned in toolbar */
  rightAlignItems?: GenericToolbarItem[];
  /** Left & right padding px value around the Toolbar container */
  leftRightPadding?: number;
  /** Test id for the Toolbar container */
  testId?: string;
  /** DOM className for the Toolbar container */
  className?: string;
  /** DOM id for the Toolbar container */
  id?: string;
  /** Ref to the Toolbar container */
  toolbarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * This toolbar component is primarily responsible for...
 * 1. Rendering the correct toolbar items based on the item props.
 * 2. Focus management. We use roving tab index, meaning that at any given time, only one button in the toobar
 *    will be tabbable. Some of the focus code here is based on the `TextFormatterToolbar` component
 *    in the Widgets library.
 */
export function GenericToolbar(props: GenericToolbarProps): React.ReactElement {
  const colorTheme: ToolbarColorTheme = useToolbarColorThemeContext();

  const [focusedButtonId, setFocusedButtonId] = React.useState<string>('');

  const internalToolbarRef = React.useRef<HTMLDivElement>(null);
  const toolbarRef = props.toolbarRef || internalToolbarRef;
  const focusWasWithinToolbar = toolbarRef.current?.contains(document.activeElement);

  React.useEffect(() => {
    const buttonToFocus = getButtonToFocus(toolbarRef, focusedButtonId);
    if (focusWasWithinToolbar && !toolbarRef.current?.contains(document.activeElement)) {
      buttonToFocus?.focus();
    }
    setFocusedButtonId(buttonToFocus?.id || focusedButtonId);
  }, [focusWasWithinToolbar, props.leftAlignItems, props.rightAlignItems, focusedButtonId, toolbarRef]);

  const handleFocus: React.FocusEventHandler<HTMLElement> = (e) => {
    if (e.target !== e.currentTarget) {
      // ignore focus events that bubble out of menus
      if (e.currentTarget.contains(e.target)) {
        setFocusedButtonId(e.target.id || focusedButtonId);
      }
    }
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.target !== e.currentTarget) {
      return;
    }

    // forward focus to the last-active button
    const buttonToFocus = getButtonToFocus(toolbarRef, focusedButtonId);
    if (buttonToFocus) {
      buttonToFocus.focus();
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    const focusableToolbarButtons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>(focusableButtonSelector));
    const focusableToolbarItemCount: number = focusableToolbarButtons.length;
    let focusIndex = focusableToolbarButtons.indexOf(e.target as HTMLButtonElement);

    if (e.key === 'ArrowLeft') {
      focusIndex = wrap(focusIndex - 1, 0, focusableToolbarItemCount - 1);
    } else if (e.key === 'ArrowRight') {
      focusIndex = wrap(focusIndex + 1, 0, focusableToolbarItemCount - 1);
    } else if (e.key === 'Home') {
      focusIndex = 0;
    } else if (e.key === 'End') {
      focusIndex = focusableToolbarItemCount - 1;
    } else {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    focusableToolbarButtons[focusIndex].focus();
  };

  return (
    <div
      id={props.id}
      className={classes(GenericToolbarStyleName, props.className)}
      data-tb-test-id={props.testId}
      style={{
        ...ItemContainerStyles,
        outline: 'none',
        backgroundColor: colorTheme.ToolbarBackground,
        justifyContent: 'space-between',
        padding: `0 ${props.leftRightPadding ?? 0}px`,
      }}
      aria-label={Messages.visualizationControls()}
      ref={toolbarRef}
      role="toolbar"
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      tabIndex={-1}
    >
      <ToolbarSide items={props.leftAlignItems} key="left" currentFocusedItemId={focusedButtonId} />
      <ToolbarSide items={props.rightAlignItems} key="right" currentFocusedItemId={focusedButtonId} />
    </div>
  );
}
GenericToolbar.displayName = 'GenericToolbar';

export const getButtonToFocus = (toolbarRef: React.RefObject<HTMLDivElement>, focusedButtonId?: string): HTMLElement | null =>
  toolbarRef.current!.querySelector<HTMLElement>(`button${focusedButtonId ? '#' + focusedButtonId : ''}:enabled:not(.hidden)`) ||
  toolbarRef.current!.querySelector<HTMLElement>(focusableButtonSelector);

const ToolbarButtonSwitcher: React.FC<{ item: GenericToolbarItem; currentFocusedId: string }> = ({ item, currentFocusedId }) => {
  if (item.isSeparator) {
    return <Separator />;
  }

  let tabIndex = item.id === currentFocusedId ? 0 : -1;

  const commonProps: Pick<
    GenericToolbarItem,
    'description' | 'disabled' | 'isHidden' | 'iconId' | 'id' | 'label' | 'showLabel' | 'testId'
  > = {
    description: item.description,
    disabled: item.disabled,
    isHidden: item.isHidden,
    iconId: item.iconId,
    id: item.id,
    label: item.label,
    showLabel: item.showLabel,
    testId: item.testId,
  };

  if (item.itemType === 'splitButton') {
    const dropdownHasFocus = currentFocusedId === item.id + SplitSecondaryIdSuffix;
    return (
      <SplitButton
        {...commonProps}
        dropdownItems={item.nestedItems ?? []}
        dropdownLabel={item.secondaryLabel ?? ''}
        onClick={item.onClick}
        tabIndex={tabIndex}
        dropdownTabIndex={dropdownHasFocus ? 0 : -1}
      />
    );
  }

  if (item.itemType === 'popoverButton') {
    return (
      <PopoverButton
        {...commonProps}
        footerButtons={item.itemPopoverProps?.footerButtons}
        popoverAlertType={item.itemPopoverProps?.popoverAlertType}
        // Show an empty popover if no content is supplied
        popoverContent={item.itemPopoverProps?.popoverContent ?? ''}
        popoverTitle={item.itemPopoverProps?.title ?? item.label}
        showPopoverTitleBar={!!item.itemPopoverProps?.showTitleBar}
        onToolbarItemClick={item.onClick}
        tabIndex={tabIndex}
      />
    );
  }

  if (item.nestedItems) {
    if (item.nestedItems.length > 1 || item.isOverflow) {
      return <Dropdown {...commonProps} isOverflow={item.isOverflow} dropdownItems={item.nestedItems} tabIndex={tabIndex} />;
    } else if (item.nestedItems.length === 1) {
      // if only 1 nested item, don't show a dropdown -- show the nested item as a button
      const shouldHideGroup = item.isHidden; // grab `isHidden` from parent
      item = item.nestedItems[0];
      item.isHidden = shouldHideGroup;
      tabIndex = item.id === currentFocusedId ? 0 : -1;
    }
  }

  return (
    <Button
      buttonType={item.itemType}
      description={item.description}
      disabled={item.disabled}
      iconId={item.iconId}
      id={item.id}
      isHidden={item.isHidden}
      label={item.label}
      selected={item.selected}
      testId={item.testId}
      onClick={item.onClick}
      showLabel={item.showLabel}
      tabIndex={tabIndex}
    />
  );
};

const ToolbarSide: React.FC<{ items?: GenericToolbarItem[]; currentFocusedItemId: string }> = ({
  items,
  currentFocusedItemId: currentFocusedId,
}) =>
  !items?.length ? null : (
    <div style={ItemContainerStyles}>
      {items.map((item, index) => {
        return (
          <div
            key={item.isSeparator ? `separator${index}` : String(item.id) + '-wrapper'}
            className={
              item.isOverflow
                ? classes(ToolbarOverflowItemClass, { [HiddenClassName]: item.isHidden })
                : classes(ToolbarItemClass, item.region, { [HiddenClassName]: item.isHidden })
            }
            style={{ height: '100%' }}
          >
            <ToolbarButtonSwitcher item={item} currentFocusedId={currentFocusedId} key={item.id} />
          </div>
        );
      })}
    </div>
  );

/**
 * Returns the value, or the min or max if the value is outside those bounds (which are inclusive).
 * If the value falls outside the bounds, it wraps to the other end.
 *
 * This is lifted right out of the Widgets library `Math.ts`
 */
function wrap(value: number, min: number, max: number): number {
  if (max < min || isNaN(value) || isNaN(min) || isNaN(max)) {
    return NaN;
  }

  if (value > max) {
    return min;
  } else if (value < min) {
    return max;
  } else {
    return value;
  }
}
